import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
  ChangeEvent,
} from "react";
import { useMap } from "@vis.gl/react-google-maps";
import {
  Button,
  Dialog,
  DropdownMenu,
  enqueueToaster,
  FooterAction,
  IconButton,
  TextField,
} from "@deere/isg.component-library";
import {
  IconChevronLeft,
  IconCircleClose,
  IconDirections,
  IconVideoPlay,
  IconPause,
  IconEdit,
  IconPanelOn,
  IconAddColumn,
  IconAdd,
  Dumptruck,
  Excavator,
  Crawler,
  SkidSteer,
  Truck,
  Loader,
  IconSave,
  IconAutomation,
  IconDelete,
  IconMarkerEmpty,
  IconJobDetailsGuidance,
  StatusAutonomyOff,
  IconDownload,
  IconHelp,
  StatusAutonomyActive,
  StatusAutonomyNotReady,
  Clock,
} from "@deere/icons/new";
import { createRoot } from "react-dom/client";
import { scaleGeoPointsToSvg } from "../../utils/scaleGeoPoints";
import { resolveTaskLocations } from "../../utils/resolveTaskLocations";
import useApiCall from "../../../hooks/useApiCall";
import "./MissionPanel.css";
import { hasPermission } from "../../../../permissions";
import { AppContext } from "../../../context/AppContext";

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

function findMachinesRunningMission(missionName: string, orgDataSession: any): string[] {
  if (!missionName || !orgDataSession || typeof orgDataSession !== "object") return [];
  const matches: string[] = [];

  for (const category of Object.keys(orgDataSession)) {
    const catObj = orgDataSession[category];
    if (!catObj || typeof catObj !== "object" || Array.isArray(catObj)) continue;

    for (const modelKey of Object.keys(catObj)) {
      const modelObj = catObj[modelKey];
      if (!modelObj || typeof modelObj !== "object" || Array.isArray(modelObj)) continue;

      for (const machineName of Object.keys(modelObj)) {
        const machine = modelObj[machineName];
        const activeName = machine?.Orchestrator?.ActiveMissionName;
        if (activeName === undefined || activeName === null) continue;

        const nameStr =
          typeof activeName === "string" ? activeName : activeName?.value || activeName?.data || "";
        if (nameStr !== missionName) continue;

        const stateRaw = machine?.VADC?.autonomy?.state?.[0];
        const autonomyState =
          typeof stateRaw === "number"
            ? stateRaw
            : typeof stateRaw === "string"
              ? Number(stateRaw)
              : NaN;
        if (autonomyState === 2) {
          matches.push(`${category}/${modelKey}/${machineName}`);
        }
      }
    }
  }
  return matches;
}

// Polyline Component
const Polyline = ({ points, stroke = "black", strokeWidth = 2 }: any) => (
  <svg width="32" height="32" viewBox="0 0 200 200">
    <polyline points={points} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
  </svg>
);

interface PolygonProps {
  points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
  fill?: string; // Optional fill color for the polygon
  stroke?: string; // Optional stroke color for the polygon
  strokeWidth?: number; // Optional stroke width for the polygon
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue", // Default fill color
  stroke = "black", // Default stroke color
  strokeWidth = 1, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

// MissionPanel Component
const MissionPanel = ({
  currentTopicString,
  curMissionObject,
  setCurMissionObject,
  orgData,
  orgDataSession,
  hideSideBar,
  curMission,
  handleClose,
  changeFirstPanel,
  panelMode,
  togglePanelMode,
  missionPreviewActive,
  setMissionPreviewActive,
  missionPreviewPaths,
  setMissionPreviewPaths,
  missionPreviewPoints,
  setMissionPreviewPoints,
}: {
  currentTopicString: any;
  curMissionObject: any;
  setCurMissionObject: (object: any) => void;
  orgData: any;
  orgDataSession: any;
  hideSideBar: () => void;
  curMission: any;
  handleClose: () => void;
  changeFirstPanel: () => void;
  panelMode: "one" | "two";
  togglePanelMode: () => void;
  missionPreviewActive: boolean;
  setMissionPreviewActive: (active: boolean) => void;
  missionPreviewPaths: any[];
  setMissionPreviewPaths: (paths: any[]) => void;
  missionPreviewPoints: any[];
  setMissionPreviewPoints: (points: any[]) => void;
}) => {
  const { user } = React.useContext(AppContext);
  const role = user.role;
  const [mission, setMission] = useState(null);
  const [value, setValue] = useState("1");
  const [tasks, setTasks] = useState<any[]>([]);
  const [editMissionObject, setEditMissionObject] = useState<any>({});
  const [vehicleObjects, setVehicleObjects] = useState({});
  const [missionRoutes, setMissionRoutes] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [locationItems, setLocationItems] = useState<any>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isTouchDrag, setIsTouchDrag] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const { execute: executeSave, isLoading: isSaving } = useApiCall({ errorAsToast: true });
  const { execute: executeDelete, isLoading: isDeleting } = useApiCall({ errorAsToast: true });
  const [isExporting, setIsExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Compute machines currently running this mission — memoized + gated on
  // dialog open so we don't scan orgDataSession on every parent render.
  const activeMachinesRunningMission = useMemo(() => {
    if (!showDeleteDialog) return [];
    return findMachinesRunningMission(editMissionObject?.missionName, orgDataSession);
  }, [showDeleteDialog, editMissionObject?.missionName, orgDataSession]);

  // Refs for animation cleanup across renders
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const vehicleMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const pointMarkersRef = useRef<Array<{ marker: google.maps.marker.AdvancedMarkerElement }>>([]);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previewPolylinesRef = useRef<google.maps.Polyline[]>([]);
  const executeOverlayRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const loopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animateMarkerFnRef = useRef<(() => void) | null>(null);
  const dashIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPausedRef = useRef(false);
  const isAnimatingRef = useRef(false);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  // Keep isAnimatingRef in sync with isAnimating state
  useEffect(() => {
    isAnimatingRef.current = isAnimating;
  }, [isAnimating]);

  const isTaskUnconfigured = (task: any): boolean => {
    if (task.action !== "Navigate" && task.action !== "Command") return true;
    if (task.action === "Navigate" && !task.properties?.uuid) return true;
    if (task.action === "Command" && !task.properties?.workType) return true;
    if (
      task.action === "Command" &&
      task.properties?.workType === "GetLoaded" &&
      task.properties?.loadSide === undefined
    )
      return true;
    return false;
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    const newTasks = [...tasks];
    const draggedTask = newTasks[draggedIndex];
    newTasks.splice(draggedIndex, 1);
    newTasks.splice(index, 0, draggedTask);

    setTasks(newTasks);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const HOLD_DURATION_MS = 300;
  const MOVE_CANCEL_THRESHOLD_PX = 10;
  const EDGE_SCROLL_ZONE_PX = 44;
  const MAX_SCROLL_SPEED_PX = 11;
  const holdTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const isDragActiveRef = useRef(false);
  const draggedIndexRef = useRef<number | null>(null);
  const pointerXRef = useRef(0);
  const pointerYRef = useRef(0);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const autoScrollRafRef = useRef<number | null>(null);
  const touchInProgressRef = useRef(false);
  const dragGhostRef = useRef<HTMLElement | null>(null);
  const ghostOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Floating clone of the card that follows the finger during a touch drag,
  // mimicking the native HTML5 drag "ghost image" (which touch doesn't provide).
  const createDragGhost = (cardEl: HTMLElement, clientX: number, clientY: number) => {
    const rect = cardEl.getBoundingClientRect();
    const ghost = cardEl.cloneNode(true) as HTMLElement;
    ghost.style.position = "fixed";
    ghost.style.left = "0";
    ghost.style.top = "0";
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.margin = "0";
    ghost.style.opacity = "0.95";
    ghost.style.pointerEvents = "none";
    ghost.style.zIndex = "9999";
    ghost.style.boxShadow = "0px 10px 24px rgba(0, 0, 0, 0.3)";
    ghost.style.transition = "none";
    ghost.style.transform = `translate(${rect.left}px, ${rect.top}px) scale(1.03)`;
    ghostOffsetRef.current = { x: clientX - rect.left, y: clientY - rect.top };
    document.body.appendChild(ghost);
    dragGhostRef.current = ghost;
  };

  const moveDragGhost = (clientX: number, clientY: number) => {
    const ghost = dragGhostRef.current;
    if (!ghost) return;
    const x = clientX - ghostOffsetRef.current.x;
    const y = clientY - ghostOffsetRef.current.y;
    ghost.style.transform = `translate(${x}px, ${y}px) scale(1.03)`;
  };

  const removeDragGhost = () => {
    if (dragGhostRef.current) {
      dragGhostRef.current.remove();
      dragGhostRef.current = null;
    }
  };

  const clearHoldTimer = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const performReorder = (targetIndex: number) => {
    const from = draggedIndexRef.current;
    if (from === null || from === targetIndex) return;
    setTasks((prev) => {
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    draggedIndexRef.current = targetIndex;
    setDraggedIndex(targetIndex);
  };

  const reorderFromPoint = (x: number, y: number) => {
    // Hide the dragged tile from hit-testing only for this elementFromPoint
    // call (not persistently), so it stays interactive: a long-press then
    // targets our own tile (which we suppress) instead of falling through to
    // whatever is behind it (e.g. a link inside the persistent-header iframe).
    const from = draggedIndexRef.current;
    const draggedEl =
      from !== null
        ? (document.querySelector(`.TaskContainer[data-index="${from}"]`) as HTMLElement | null)
        : null;
    const prevPointerEvents = draggedEl ? draggedEl.style.pointerEvents : "";
    if (draggedEl) draggedEl.style.pointerEvents = "none";
    const element = document.elementFromPoint(x, y);
    if (draggedEl) draggedEl.style.pointerEvents = prevPointerEvents;
    const taskContainer = element?.closest(".TaskContainer");
    if (!taskContainer) return;
    const indexStr = taskContainer.getAttribute("data-index");
    if (indexStr === null) return;
    const index = parseInt(indexStr, 10);
    if (!Number.isNaN(index)) performReorder(index);
  };

  const stopAutoScroll = () => {
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  };

  const autoScrollStep = () => {
    const container = scrollContainerRef.current;
    if (!container || !isDragActiveRef.current) {
      autoScrollRafRef.current = null;
      return;
    }
    const rect = container.getBoundingClientRect();
    const y = pointerYRef.current;
    // Base the scroll decision on the task tiles themselves, not the whole
    // panel (which also contains buttons/space below the list). Only scroll
    // when an actual tile is hidden past the viewport edge in that direction,
    // i.e. the dragged tile has reached the top/bottom of the visible list.
    const tiles = container.querySelectorAll(".TaskContainer");
    const firstTile = tiles[0] as HTMLElement | undefined;
    const lastTile = tiles[tiles.length - 1] as HTMLElement | undefined;
    if (!firstTile || !lastTile) {
      autoScrollRafRef.current = requestAnimationFrame(autoScrollStep);
      return;
    }
    const moreAbove = firstTile.getBoundingClientRect().top < rect.top - 1;
    const moreBelow = lastTile.getBoundingClientRect().bottom > rect.bottom + 1;
    let delta = 0;
    if (y < rect.top + EDGE_SCROLL_ZONE_PX && moreAbove) {
      const intensity = Math.min((rect.top + EDGE_SCROLL_ZONE_PX - y) / EDGE_SCROLL_ZONE_PX, 1);
      delta = -MAX_SCROLL_SPEED_PX * intensity;
    } else if (y > rect.bottom - EDGE_SCROLL_ZONE_PX && moreBelow) {
      const intensity = Math.min((y - (rect.bottom - EDGE_SCROLL_ZONE_PX)) / EDGE_SCROLL_ZONE_PX, 1);
      delta = MAX_SCROLL_SPEED_PX * intensity;
    }
    if (delta !== 0) {
      const before = container.scrollTop;
      container.scrollTop += delta;
      if (container.scrollTop !== before) {
        reorderFromPoint(pointerXRef.current, y);
      }
    }
    autoScrollRafRef.current = requestAnimationFrame(autoScrollStep);
  };

  const ensureAutoScroll = () => {
    if (autoScrollRafRef.current === null) {
      autoScrollRafRef.current = requestAnimationFrame(autoScrollStep);
    }
  };

  const handleTouchStart = (index: number, e: React.TouchEvent) => {
    const touch = e.touches[0];
    const cardEl = e.currentTarget as HTMLElement;
    touchInProgressRef.current = true;
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
    pointerXRef.current = touch.clientX;
    pointerYRef.current = touch.clientY;
    scrollContainerRef.current = (e.currentTarget as HTMLElement).closest(
      ".MissionPanel",
    ) as HTMLElement | null;
    isDragActiveRef.current = false;
    clearHoldTimer();
    holdTimerRef.current = setTimeout(() => {
      isDragActiveRef.current = true;
      draggedIndexRef.current = index;
      setDraggedIndex(index);
      setIsTouchDrag(true);
      createDragGhost(cardEl, pointerXRef.current, pointerYRef.current);
    }, HOLD_DURATION_MS);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!isDragActiveRef.current) {
      if (touchStartPosRef.current && holdTimerRef.current) {
        const dx = touch.clientX - touchStartPosRef.current.x;
        const dy = touch.clientY - touchStartPosRef.current.y;
        if (Math.hypot(dx, dy) > MOVE_CANCEL_THRESHOLD_PX) {
          clearHoldTimer();
        }
      }
      return;
    }

    pointerXRef.current = touch.clientX;
    pointerYRef.current = touch.clientY;
    moveDragGhost(touch.clientX, touch.clientY);
    reorderFromPoint(touch.clientX, touch.clientY);
    ensureAutoScroll();
  };

  const handleTouchEnd = () => {
    clearHoldTimer();
    stopAutoScroll();
    removeDragGhost();
    isDragActiveRef.current = false;
    draggedIndexRef.current = null;
    touchStartPosRef.current = null;
    touchInProgressRef.current = false;
    setIsTouchDrag(false);
    handleDragEnd();
  };

  // Cleanup any pending hold timer / auto-scroll frame on unmount.
  useEffect(() => {
    return () => {
      clearHoldTimer();
      stopAutoScroll();
      removeDragGhost();
    };
  }, []);

  // While a finger is pressing a task card, suppress the browser's long-press
  // context menu and native drag. The dragged card uses pointer-events: none,
  // so the long-press can otherwise fall through to an element behind it (e.g.
  // a link in the persistent header), opening an "open link in new tab" menu.
  useEffect(() => {
    const suppress = (e: Event) => {
      if (touchInProgressRef.current || isDragActiveRef.current) {
        e.preventDefault();
      }
    };
    document.addEventListener("contextmenu", suppress, { capture: true });
    document.addEventListener("dragstart", suppress, { capture: true });
    return () => {
      document.removeEventListener("contextmenu", suppress, { capture: true } as EventListenerOptions);
      document.removeEventListener("dragstart", suppress, { capture: true } as EventListenerOptions);
    };
  }, []);

  const [commandItems, setCommandItems] = useState<any>([
    {
      onClick: () => {
        // Note that this doesn't need to be set, as its set in the jsx inline later
      },
      id: 0,
      title: "Dump",
      subtitle: "Raise and Lower Bin",
      icon: (
        <Dumptruck style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }} />
      ),
      properties: {
        workType: "RasieAndLowerBin",
      },
    },
    {
      onClick: () => {
        // Note that this doesn't need to be set, as its set in the jsx inline later
      },
      id: 1,
      title: "Work On",
      subtitle: "Turn on working function",
      icon: (
        <StatusAutonomyActive
          style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }}
        />
      ),
      properties: {
        workType: "WorkOn",
      },
    },
    {
      onClick: () => {
        // Note that this doesn't need to be set, as its set in the jsx inline later
      },
      id: 2,
      title: "Work Off",
      subtitle: "Turn off working function",
      icon: (
        <StatusAutonomyNotReady
          style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }}
        />
      ),
      properties: {
        workType: "WorkOff",
      },
    },
    {
      onClick: () => {
        // Note that this doesn't need to be set, as its set in the jsx inline later
      },
      id: 3,
      title: "Wait to Enter",
      subtitle: "Wait until entry condition is met",
      icon: (
        <Clock style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }} />
      ),
      properties: {
        workType: "WaitToEnter",
      },
    },
    {
      onClick: () => {
        // Note that this doesn't need to be set, as its set in the jsx inline later
      },
      id: 4,
      title: "Load Truck",
      subtitle: "Load material into truck",
      icon: (
        <Loader style={{ minHeight: "32px", minWidth: "32px", height: "auto", width: "auto" }} />
      ),
      properties: {
        workType: "GetLoaded",
      },
    },
  ]);
  const map = useMap("mainMap");

  useEffect(() => {
    // Check basic fields only — task-level issues are caught on Save click
    const hasBasicFields =
      editMissionObject?.missionId &&
      editMissionObject?.missionName !== "" &&
      editMissionObject?.missionType !== "" &&
      editMissionObject?.missionDescription !== "" &&
      editMissionObject?.taskRepetitions !== "" &&
      tasks?.length > 0;

    setIsDisabled(!hasBasicFields);
  }, [editMissionObject, tasks]);

  // Stop any active preview: clear interval, remove markers, polylines, reset state
  const stopPreview = useCallback(() => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }
    if (dashIntervalRef.current) {
      clearInterval(dashIntervalRef.current);
      dashIntervalRef.current = null;
    }
    if (vehicleMarkerRef.current) {
      vehicleMarkerRef.current.map = null;
      vehicleMarkerRef.current = null;
    }
    if (executeOverlayRef.current) {
      executeOverlayRef.current.map = null;
      executeOverlayRef.current = null;
    }
    previewPolylinesRef.current.forEach((pl) => pl.setMap(null));
    previewPolylinesRef.current = [];
    pointMarkersRef.current.forEach(({ marker }) => {
      marker.map = null;
    });
    pointMarkersRef.current = [];
    animateMarkerFnRef.current = null;
    setMissionPreviewPaths([]);
    setMissionPreviewPoints([]);
    setMissionPreviewActive(false);
    setIsAnimating(false);
    setIsPaused(false);
    isPausedRef.current = false;
    isAnimatingRef.current = false;
  }, [setMissionPreviewActive, setMissionPreviewPaths, setMissionPreviewPoints]);

  // Stop preview when the selected mission changes
  useEffect(() => {
    stopPreview();
  }, [curMissionObject]);

  // Cleanup preview state when component unmounts
  useEffect(() => {
    return () => {
      stopPreview();
    };
  }, [stopPreview]);

  // Restart preview from beginning when tasks change
  useEffect(() => {
    if (!isAnimatingRef.current) return;
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }
    restartTimeoutRef.current = setTimeout(() => {
      stopPreview();
      animateRoute();
    }, 300);
    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
    };
  }, [tasks]);

  // Helper Functions
  const checkIfDisabled = () => {
    // setIsDisabled(!editMissionObject?.guidanceRoute);
  };

  const saveMission = async () => {
    // Validate task-level configuration
    const hasUnconfiguredTasks = tasks.some(isTaskUnconfigured);

    if (hasUnconfiguredTasks) {
      setHasAttemptedSave(true);
      enqueueToaster(
        "Tasks are incomplete. Please ensure each task has a destination or command selected.",
        "Incomplete Tasks",
        { variant: "warning" },
      );
      return;
    }

    // Strip resolved data: keep only uuid for Navigate, workType for Command
    const cleanTasks = tasks.map((task) => {
      if (task.action === "Navigate") {
        return {
          title: task.title,
          action: "Navigate",
          properties: { uuid: task.properties?.uuid },
        };
      } else if (task.action === "Command") {
        const props: any = { workType: task.properties?.workType };
        if (task.properties?.workType === "GetLoaded" && task.properties?.loadSide !== undefined) {
          props.loadSide = task.properties.loadSide;
        }
        return { title: task.title, action: "Command", properties: props };
      }
      const { icon, navigateItem, ...rest } = task;
      return rest;
    });
    const success = await executeSave(
      () =>
        fetch(`https://${backendURI}/Save/Mission`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            missionObject: { ...editMissionObject, tasks: cleanTasks },
            currentTopicString: `${currentTopicString}Missions/${editMissionObject.missionId}`,
          }),
        }),
      { successMessage: "Mission saved successfully." },
    );
    if (success) {
      handleBack();
    }
  };

  const deleteMission = async () => {
    if (!editMissionObject?.missionId) return;
    const success = await executeDelete(
      () =>
        fetch(`https://${backendURI}/Save/DeleteMission`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            currentTopicString: `${currentTopicString}Missions/${editMissionObject.missionId}`,
          }),
        }),
      { successMessage: "Mission deleted successfully.", successTitle: "" },
    );
    if (success) {
      setShowDeleteDialog(false);
      handleBack();
    }
  };

  const exportMission = async () => {
    // Block export if any task is unconfigured (same check as save)
    if (tasks.some(isTaskUnconfigured)) {
      enqueueToaster(
        "Tasks are incomplete. Please configure all tasks before exporting.",
        "Export Blocked",
        { variant: "error" },
      );
      setHasAttemptedSave(true);
      return;
    }

    setIsExporting(true);
    try {
      // Build tasks with only title, action, and the relevant properties field
      const cleanTasks = tasks.map((task) => {
        if (task.action === "Navigate") {
          return {
            title: task.title,
            action: "Navigate",
            properties: { uuid: task.properties?.uuid },
          };
        } else if (task.action === "Command") {
          const props: any = { workType: task.properties?.workType };
          if (
            task.properties?.workType === "GetLoaded" &&
            task.properties?.loadSide !== undefined
          ) {
            props.loadSide = task.properties.loadSide;
          }
          return { title: task.title, action: "Command", properties: props };
        }
        return { title: task.title, action: task.action, properties: task.properties };
      });
      const missionToExport = {
        missionName: editMissionObject.missionName,
        missionType: editMissionObject.missionType,
        missionDescription: editMissionObject.missionDescription,
        taskRepetitions: editMissionObject.taskRepetitions || "1",
        tasks: cleanTasks,
      };
      const blob = new Blob([JSON.stringify(missionToExport, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${editMissionObject.missionName || "mission"}_${editMissionObject.missionId || "export"}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueToaster(
        `"${editMissionObject.missionName || "Mission"}" downloaded successfully.`,
        "Exported",
        { variant: "success" },
      );
    } catch (error) {
      enqueueToaster("Failed to export mission. Please try again.", "Export Error", {
        variant: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const animateRoute = async () => {
    if (!map || tasks.length === 0) return;

    // Prevent multiple animations from running
    if (isAnimatingRef.current) {
      return;
    }

    setIsAnimating(true);
    isAnimatingRef.current = true;
    setIsPaused(false);
    isPausedRef.current = false;

    const AUTONOMY_BLUE = "#068afc";
    const AUTONOMY_BLUE_LIGHT = "rgba(6, 138, 252, 0.25)";

    const allPoints: any[] = [];
    const missionPathData: any[] = [];
    const missionPoints: any[] = [];
    // For each command entry pushed into `missionPoints` (in task order), record
    // the index in `allPoints` of the most recent Navigate destination. This
    // preserves task ordering (Execute -> Navigate -> Execute) when mapping
    // pointMarkers to interpolated-path arrival indices later.
    const commandNavIndices: number[] = [];
    let lastNavigationPoint: any = null; // Track the last point from Navigate tasks

    // Collect all points from each task
    tasks.forEach((task) => {
      // Handle Navigate tasks
      if (task.navigateItem && task?.action === "Navigate") {
        // Collect the full path data from properties
        if (task.properties || task.navigateItem.properties) {
          const pathProperties = task.properties || task.navigateItem.properties;

          // Create path data object with all necessary information
          const pathData = {
            uuid: pathProperties.uuid,
            points: pathProperties.points,
            pathType: pathProperties.pathType,
            description: pathProperties.description,
            deleted: pathProperties.deleted,
          };

          missionPathData.push(pathData);
        }

        const isZone = task.properties.zoneType && task.properties.points;

        if (isZone) {
          // For zones, compute centroid — the machine navigates to/from the center
          const zonePoints = task.properties.points;
          const coords: { lat: number; lng: number }[] = [];
          zonePoints.forEach((point: any) => {
            if (
              point.location &&
              !isNaN(point.location.latitude) &&
              !isNaN(point.location.longitude)
            ) {
              coords.push({ lat: point.location.latitude, lng: point.location.longitude });
            }
          });
          if (coords.length > 0) {
            let centroid = { latitude: 0, longitude: 0 };
            if (coords.length >= 3) {
              let signedArea = 0;
              let cx = 0;
              let cy = 0;
              for (let ci = 0; ci < coords.length; ci++) {
                const x0 = coords[ci].lng;
                const y0 = coords[ci].lat;
                const x1 = coords[(ci + 1) % coords.length].lng;
                const y1 = coords[(ci + 1) % coords.length].lat;
                const cross = x0 * y1 - x1 * y0;
                signedArea += cross;
                cx += (x0 + x1) * cross;
                cy += (y0 + y1) * cross;
              }
              signedArea *= 0.5;
              if (Math.abs(signedArea) > 1e-12) {
                cx /= 6 * signedArea;
                cy /= 6 * signedArea;
                centroid = { latitude: cy, longitude: cx };
              } else {
                centroid = {
                  latitude: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
                  longitude: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
                };
              }
            } else {
              centroid = {
                latitude: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
                longitude: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
              };
            }
            allPoints.push({ location: centroid, action: task.action, isZone: true });
            missionPoints.push({ location: centroid, action: task.action });
            lastNavigationPoint = centroid;
          }
        } else if (
          task.properties.location &&
          !isNaN(task.properties.location.longitude) &&
          !isNaN(task.properties.location.latitude)
        ) {
          allPoints.push({ location: task.properties.location, action: task.action });
          missionPoints.push({ location: task.properties.location, action: task.action });
          lastNavigationPoint = task.properties.location;
        } else if (task.properties.points) {
          const points = task.properties.points;
          points.forEach((point: any) => {
            if (
              point.location &&
              !isNaN(point.location.longitude) &&
              !isNaN(point.location.latitude)
            ) {
              allPoints.push(point);
              missionPoints.push({ location: point.location, action: task.action });
              lastNavigationPoint = point.location;
            }
          });
        }
      }
      // Handle Load/Dump with specific action type
      else if (task?.action === "Load" || task?.action === "Dump") {
        if (
          task.properties?.location &&
          !isNaN(task.properties.location.longitude) &&
          !isNaN(task.properties.location.latitude)
        ) {
          missionPoints.push({ location: task.properties.location, action: task.action });
          commandNavIndices.push(allPoints.length - 1);
        } else if (task.loadItem?.properties?.location) {
          missionPoints.push({ location: task.loadItem.properties.location, action: task.action });
          commandNavIndices.push(allPoints.length - 1);
        } else if (task.dumpItem?.properties?.location) {
          missionPoints.push({ location: task.dumpItem.properties.location, action: task.action });
          commandNavIndices.push(allPoints.length - 1);
        }
      }
      // Handle Command tasks (which include Load and Dump)
      else if (task?.action === "Command") {
        let actionType = null;
        if (task.title?.toLowerCase().includes("load")) {
          actionType = "Load";
        } else if (task.title?.toLowerCase().includes("dump")) {
          actionType = "Dump";
        } else if (task.title?.toLowerCase().includes("work on")) {
          actionType = "Work On";
        } else if (task.title?.toLowerCase().includes("work off")) {
          actionType = "Work Off";
        } else if (task.title?.toLowerCase().includes("wait")) {
          actionType = "Wait";
        }

        if (actionType && lastNavigationPoint) {
          missionPoints.push({ location: lastNavigationPoint, action: actionType });
          commandNavIndices.push(allPoints.length - 1);
        }
      }
    });

    // If no navigable points were resolved, there's nothing to animate
    if (allPoints.length === 0) {
      setIsAnimating(false);
      return;
    }

    // Set mission preview data — for zones we do NOT highlight the zone boundary.
    // Instead we only highlight path-type features.
    const pathOnlyData = missionPathData.filter((pd) => !pd.zoneType);
    setMissionPreviewPaths(pathOnlyData);
    setMissionPreviewPoints(missionPoints);

    // Enable preview mode AFTER setting the data
    setMissionPreviewActive(true);

    // Wait a frame to ensure map styles are updated
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Auto-zoom the main map to fit the mission's full path so the preview
    // is visible regardless of the user's prior viewport.
    if (allPoints.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      allPoints.forEach((p) => {
        if (p?.location?.latitude != null && p?.location?.longitude != null) {
          bounds.extend(new google.maps.LatLng(p.location.latitude, p.location.longitude));
        }
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 80);
      }
    }

    // --- Build segment data for animated directional polylines ---
    const segmentCoords: google.maps.LatLngLiteral[][] = [];
    for (let i = 0; i < allPoints.length - 1; i++) {
      const start = allPoints[i].location;
      const end = allPoints[i + 1].location;
      segmentCoords.push([
        { lat: start.latitude, lng: start.longitude },
        { lat: end.latitude, lng: end.longitude },
      ]);
    }

    // Draw static base polyline
    const fullPath = allPoints.map((p) => ({
      lat: p.location.latitude,
      lng: p.location.longitude,
    }));
    const baseLine = new google.maps.Polyline({
      path: fullPath,
      strokeColor: AUTONOMY_BLUE,
      strokeOpacity: 0.45,
      strokeWeight: 6,
      zIndex: 40,
      map,
    });
    previewPolylinesRef.current.push(baseLine);

    // Animated directional dashes — uses symbol repeating along polyline
    const dashSymbol: google.maps.Symbol = {
      path: "M 0,-1 0,1",
      scale: 4.5,
      strokeColor: "#ffffff",
      strokeWeight: 4,
      strokeOpacity: 0.9,
    };
    const animatedLine = new google.maps.Polyline({
      path: fullPath,
      strokeOpacity: 0,
      zIndex: 46,
      icons: [
        {
          icon: dashSymbol,
          offset: "0%",
          repeat: "20px",
        },
      ],
      map,
    });
    previewPolylinesRef.current.push(animatedLine);

    // Animate the dash offset to create flowing direction
    let dashOffset = 0;
    dashIntervalRef.current = setInterval(() => {
      dashOffset = (dashOffset + 0.15) % 100;
      const icons = animatedLine.get("icons");
      if (icons) {
        icons[0].offset = dashOffset + "%";
        animatedLine.set("icons", icons);
      }
    }, 50);

    // --- Create execute-point markers (autonomy blue theme) ---
    const pointMarkers: Array<{
      marker: google.maps.marker.AdvancedMarkerElement;
      labelDiv: HTMLDivElement;
      location: any;
      action: string;
    }> = [];

    const executeIconMap: Record<string, string> = {
      Load: "⬆",
      Dump: "⬇",
      "Work On": "⚙",
      "Work Off": "⏹",
      Wait: "⏳",
    };

    missionPoints.forEach((point) => {
      if (point.action && ["Load", "Dump", "Work On", "Work Off", "Wait"].includes(point.action)) {
        // Label pill
        const labelDiv = document.createElement("div");
        labelDiv.style.cssText = `
          background: #fff;
          color: #333;
          padding: 3px 10px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 11px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          box-shadow: 0 1px 6px rgba(0,0,0,0.22);
          white-space: nowrap;
          position: absolute;
          right: 16px;
          top: 0;
          transform: translateY(-50%);
          pointer-events: none;
        `;
        labelDiv.textContent = `${executeIconMap[point.action] || ""} ${point.action}`;

        // Circle marker
        const circleDiv = document.createElement("div");
        circleDiv.style.cssText = `
          width: 14px;
          height: 14px;
          background: #fff;
          border: 3px solid ${AUTONOMY_BLUE};
          border-radius: 50%;
          box-shadow: 0 0 0 3px ${AUTONOMY_BLUE_LIGHT}, 0 1px 4px rgba(0,0,0,0.18);
          flex-shrink: 0;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        `;

        const container = document.createElement("div");
        container.style.cssText = `
          position: relative;
          width: 0;
          height: 0;
          overflow: visible;
        `;
        container.appendChild(labelDiv);
        container.appendChild(circleDiv);

        if (!google.maps.marker?.AdvancedMarkerElement) return;
        const pointMarker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: point.location.latitude, lng: point.location.longitude },
          content: container,
          map: map,
          zIndex: 10000,
        });

        setTimeout(() => {
          const markerContent = pointMarker.content as HTMLElement;
          if (markerContent?.parentElement) {
            markerContent.parentElement.style.overflow = "visible";
            if (markerContent.parentElement.parentElement) {
              markerContent.parentElement.parentElement.style.overflow = "visible";
            }
          }
        }, 100);

        pointMarkers.push({
          marker: pointMarker,
          labelDiv: labelDiv,
          location: point.location,
          action: point.action,
        });
      }
    });

    // --- Create vehicle marker ---
    let markerDiv = document.createElement("div");
    // Offset so icon center aligns with the GPS coordinate.
    // AdvancedMarkerElement anchors at bottom-center, so shift up by half the icon height.
    markerDiv.style.transform = "translateY(50%)";
    const root = createRoot(markerDiv);
    // Determine initial heading direction for icon flip
    let initialFacingWest = false;
    if (allPoints.length >= 2) {
      const startLng = allPoints[0].location.longitude;
      const nextLng = allPoints[1].location.longitude;
      initialFacingWest = nextLng < startLng;
    }
    const renderVehicleIcon = (facingWest: boolean) => {
      root.render(
        <Dumptruck
          style={{
            height: "48px",
            width: "48px",
            display: "block",
            transform: facingWest ? "scaleX(-1)" : "scaleX(1)",
          }}
          outline={AUTONOMY_BLUE}
        />,
      );
    };
    renderVehicleIcon(initialFacingWest);
    let lastFacingWest = initialFacingWest;

    const startingLatLng = new google.maps.LatLng(
      allPoints[0].location.latitude,
      allPoints[0].location.longitude,
    );
    if (!google.maps.marker?.AdvancedMarkerElement) {
      setIsAnimating(false);
      return;
    }
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: startingLatLng,
      content: markerDiv,
      zIndex: 999999,
      map,
    });

    setTimeout(() => {
      const markerContent = marker.content as HTMLElement;
      if (markerContent?.parentElement) {
        markerContent.parentElement.style.overflow = "visible";
        if (markerContent.parentElement.parentElement) {
          markerContent.parentElement.parentElement.style.overflow = "visible";
        }
      }
    }, 100);

    // Store refs for external cleanup
    vehicleMarkerRef.current = marker;
    pointMarkersRef.current = pointMarkers;

    // --- Build interpolated points for smooth animation ---
    const interpolatedPoints: google.maps.LatLng[] = [];
    const zoneIndices: Set<number> = new Set();
    // arrivalIndices[i] = interpolated-path index when the vehicle reaches allPoints[i].
    // Used to schedule execute-task pauses in proper task order so that
    // Execute -> Navigate -> Execute plays as: pause, drive, pause (rather than
    // collapsing both pauses to the spatially-nearest interpolated index).
    const arrivalIndices: number[] = [];

    if (allPoints.length > 0 && allPoints[0].isZone) {
      zoneIndices.add(0);
    }

    if (allPoints.length > 0) {
      arrivalIndices.push(0);
    }

    for (let i = 0; i < allPoints.length - 1; i++) {
      const start = allPoints[i].location;
      const end = allPoints[i + 1].location;
      const latDiff = end.latitude - start.latitude;
      const lngDiff = end.longitude - start.longitude;
      const totalDistance = Math.sqrt(latDiff ** 2 + lngDiff ** 2);
      const steps = Math.floor(totalDistance / 0.00002);
      const beforeLen = interpolatedPoints.length;

      if (allPoints[i + 1].isZone) {
        zoneIndices.add(beforeLen + steps);
      }

      for (let j = 0; j <= steps; j++) {
        const t = j / steps;
        const interpolatedLat = start.latitude + t * latDiff;
        const interpolatedLng = start.longitude + t * lngDiff;
        if (!isNaN(interpolatedLat) && !isNaN(interpolatedLng)) {
          interpolatedPoints.push(new google.maps.LatLng(interpolatedLat, interpolatedLng));
        }
      }
      // Arrival at allPoints[i+1] is at the LAST point pushed by this segment.
      arrivalIndices.push(Math.max(0, interpolatedPoints.length - 1));
    }

    // Progress trail — shows where vehicle has been
    const trailLine = new google.maps.Polyline({
      path: [],
      strokeColor: AUTONOMY_BLUE,
      strokeOpacity: 0.7,
      strokeWeight: 6,
      zIndex: 48,
      map,
    });
    previewPolylinesRef.current.push(trailLine);

    // --- Helper: show execute overlay at a position ---
    const showExecuteOverlay = (pos: google.maps.LatLng, action: string) => {
      // Clean previous overlay
      if (executeOverlayRef.current) {
        executeOverlayRef.current.map = null;
      }
      const activeTextMap: Record<string, string> = {
        Load: "Loading",
        Dump: "Dumping",
        "Work On": "Working",
        "Work Off": "Stopping",
        Wait: "Waiting",
      };
      const overlayDiv = document.createElement("div");
      overlayDiv.style.cssText = `
        background: #fff;
        color: #333;
        padding: 6px 18px;
        border-radius: 20px;
        font-weight: 700;
        font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        box-shadow: 0 2px 12px rgba(0,0,0,0.25);
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 8px;
        transform: translateY(-20px);
        animation: missionExecFadeIn 0.3s ease;
      `;

      // Pulsing dot
      const dot = document.createElement("span");
      dot.style.cssText = `
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${AUTONOMY_BLUE};
        display: inline-block;
        animation: missionExecPulse 1s ease-in-out infinite;
      `;
      overlayDiv.appendChild(dot);

      const text = document.createElement("span");
      text.textContent = `${executeIconMap[action] || ""} ${activeTextMap[action] || action}`;
      overlayDiv.appendChild(text);

      if (!google.maps.marker?.AdvancedMarkerElement) return;
      const overlayMarker = new google.maps.marker.AdvancedMarkerElement({
        position: pos,
        content: overlayDiv,
        map,
        zIndex: 1000000,
      });

      setTimeout(() => {
        const mc = overlayMarker.content as HTMLElement;
        if (mc?.parentElement) {
          mc.parentElement.style.overflow = "visible";
          if (mc.parentElement.parentElement) {
            mc.parentElement.parentElement.style.overflow = "visible";
          }
        }
      }, 50);

      executeOverlayRef.current = overlayMarker;
    };

    const hideExecuteOverlay = () => {
      if (executeOverlayRef.current) {
        executeOverlayRef.current.map = null;
        executeOverlayRef.current = null;
      }
    };

    // --- Inject CSS keyframes for animations ---
    let styleEl = document.getElementById("mission-preview-animations");
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "mission-preview-animations";
      styleEl.textContent = `
        @keyframes missionExecPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
        @keyframes missionExecFadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(-20px); }
        }
      `;
      document.head.appendChild(styleEl);
    }

    // Map each pointMarker to the interpolated-path index it should pause at,
    // using the navigate-destination index recorded when the command was added.
    // This preserves task order: a command between two Navigates pauses at the
    // arrival of the prior Navigate, not at the spatially-nearest path point.
    // NOTE: a single arrival index can host multiple commands (back-to-back
    // commands at the same Navigate point) — queue them so each fires in turn.
    const markerPauseIndices: Map<number, number[]> = new Map();
    pointMarkers.forEach((_pm, pmIdx) => {
      const navIdx = commandNavIndices[pmIdx];
      if (navIdx == null || navIdx < 0 || navIdx >= arrivalIndices.length) return;
      const pauseIdx = arrivalIndices[navIdx];
      const queue = markerPauseIndices.get(pauseIdx);
      if (queue) queue.push(pmIdx);
      else markerPauseIndices.set(pauseIdx, [pmIdx]);
    });

    let index = 0;
    const trailPath: google.maps.LatLng[] = [];

    if (allPoints.length > 0 && allPoints[0].isZone) {
      map.panTo(startingLatLng);
      map.setZoom(17);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      zoneIndices.delete(0);
    }

    const animateMarker = () => {
      if (index < interpolatedPoints.length) {
        const currentPos = interpolatedPoints[index];
        marker.position = currentPos;

        // Update vehicle facing direction based on movement
        if (index > 0) {
          const prevPos = interpolatedPoints[index - 1];
          const lngDelta = currentPos.lng() - prevPos.lng();
          if (Math.abs(lngDelta) > 0.0000001) {
            const nowFacingWest = lngDelta < 0;
            if (nowFacingWest !== lastFacingWest) {
              lastFacingWest = nowFacingWest;
              renderVehicleIcon(nowFacingWest);
            }
          }
        }

        // Update progress trail
        trailPath.push(currentPos);
        if (index % 3 === 0) {
          trailLine.setPath(trailPath);
        }

        if (zoneIndices.has(index)) {
          zoneIndices.delete(index);
          index++;
          map.panTo(currentPos);

          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
          }

          resumeTimeoutRef.current = setTimeout(() => {
            animationIntervalRef.current = setInterval(animateMarker, 50);
          }, 2000);
          return;
        }

        // Check if this index triggers an execute-task pause. Multiple executes
        // can share an index (back-to-back Command tasks at the same Navigate
        // point); fire each one sequentially before advancing.
        const pauseQueue = markerPauseIndices.get(index);
        if (pauseQueue && pauseQueue.length > 0) {
          const pauseMarkerIdx = pauseQueue.shift() as number;
          const isLastAtIdx = pauseQueue.length === 0;
          if (isLastAtIdx) markerPauseIndices.delete(index);
          const { labelDiv, action } = pointMarkers[pauseMarkerIdx];

          // Snap vehicle to this position
          marker.position = currentPos;

          // Show execute overlay
          showExecuteOverlay(currentPos, action);

          // Highlight the point marker as active
          const circle = labelDiv.parentElement?.querySelector("div:last-child") as HTMLElement;
          if (circle) {
            circle.style.background = AUTONOMY_BLUE;
            circle.style.borderColor = "#fff";
            circle.style.boxShadow = `0 0 0 4px ${AUTONOMY_BLUE_LIGHT}, 0 0 12px ${AUTONOMY_BLUE}`;
          }

          // Pause animation
          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
          }

          // Resume after 2.5 seconds. Only advance the position index after the
          // final queued execute at this point has finished its pause.
          resumeTimeoutRef.current = setTimeout(() => {
            hideExecuteOverlay();
            if (circle) {
              circle.style.background = "#fff";
              circle.style.borderColor = AUTONOMY_BLUE;
              circle.style.boxShadow = `0 0 0 3px ${AUTONOMY_BLUE_LIGHT}, 0 1px 4px rgba(0,0,0,0.18)`;
            }
            if (isLastAtIdx) index++;
            animationIntervalRef.current = setInterval(animateMarker, 50);
          }, 2500);
          return;
        }

        index++;
      } else {
        // Animation completed — clean up and loop
        if (animationIntervalRef.current) {
          clearInterval(animationIntervalRef.current);
          animationIntervalRef.current = null;
        }
        // Brief pause before looping
        loopTimeoutRef.current = setTimeout(() => {
          // Clean up map objects but keep preview state active
          if (vehicleMarkerRef.current) {
            vehicleMarkerRef.current.map = null;
            vehicleMarkerRef.current = null;
          }
          if (executeOverlayRef.current) {
            executeOverlayRef.current.map = null;
            executeOverlayRef.current = null;
          }
          if (dashIntervalRef.current) {
            clearInterval(dashIntervalRef.current);
            dashIntervalRef.current = null;
          }
          previewPolylinesRef.current.forEach((pl) => pl.setMap(null));
          previewPolylinesRef.current = [];
          pointMarkersRef.current.forEach(({ marker }) => {
            marker.map = null;
          });
          pointMarkersRef.current = [];
          animateMarkerFnRef.current = null;
          // Allow animateRoute to start fresh
          setIsAnimating(false);
          setIsPaused(false);
          isPausedRef.current = false;
          // Re-trigger animation
          animateRoute();
        }, 800);
      }
    };
    animateMarkerFnRef.current = animateMarker;
    animationIntervalRef.current = setInterval(animateMarker, 50);
  };

  const togglePreview = () => {
    if (!isAnimating) {
      // Warn about unresolved Navigate tasks (UUID not on current map)
      const unresolved = tasks.filter((t) => t.action === "Navigate" && !t.navigateItem);
      if (unresolved.length > 0) {
        enqueueToaster(
          `${unresolved.length} destination(s) not found on the current map — skipped in preview.`,
          "Preview Warning",
          { variant: "warning" },
        );
      }
      // Start fresh animation
      animateRoute();
      return;
    }
    if (isPaused) {
      // Resume
      isPausedRef.current = false;
      setIsPaused(false);
      if (animateMarkerFnRef.current) {
        animationIntervalRef.current = setInterval(animateMarkerFnRef.current, 50);
      }
      if (dashIntervalRef.current === null) {
        // Restart dash animation on existing animated polyline
        const animatedLine = previewPolylinesRef.current.find((pl) => pl.get("icons"));
        if (animatedLine) {
          let dashOffset = parseFloat(
            (animatedLine.get("icons")?.[0]?.offset || "0").replace("%", ""),
          );
          dashIntervalRef.current = setInterval(() => {
            dashOffset = (dashOffset + 0.15) % 100;
            const icons = animatedLine.get("icons");
            if (icons) {
              icons[0].offset = dashOffset + "%";
              animatedLine.set("icons", icons);
            }
          }, 50);
        }
      }
    } else {
      // Pause
      isPausedRef.current = true;
      setIsPaused(true);
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
        loopTimeoutRef.current = null;
      }
      if (dashIntervalRef.current) {
        clearInterval(dashIntervalRef.current);
        dashIntervalRef.current = null;
      }
    }
  };

  const handleBack = () => {
    handleClose();
  };

  const handleShowHideList = () => {
    togglePanelMode();
  };

  const addTask = () => {
    setHasAttemptedSave(false);
    setTasks([...tasks, { id: Date.now(), title: "", guidancePath: [] }]);
  };

  const removeTask = (id: any) => {
    setTasks(tasks.filter((task) => task.id !== id));
  };

  useEffect(() => {
    checkIfDisabled();
  }, [editMissionObject]);

  useEffect(() => {
    setEditMissionObject(curMissionObject);
    setHasAttemptedSave(false);
    if (curMissionObject?.tasks) {
      const resolved = resolveTaskLocations(curMissionObject.tasks, orgDataSession);
      const withIds = resolved.map((task, index) => ({
        ...task,
        id: task.id ?? Date.now() + index,
      }));
      setTasks(withIds);
    } else {
      setTasks([]);
    }
    // Only re-sync when the mission identity changes, not on every orgDataSession update,
    // so that in-progress user edits are not overwritten.
  }, [curMissionObject?.missionId]);

  useLayoutEffect(() => {
    setMission(curMission);
  }, [curMission]);

  function getLocationsFromOrgData(orgDataSession: any): any[] {
    const locations: any[] = []; // Array to hold found vehicle objects

    for (const key in orgDataSession?.["Line"]) {
      locations.push({ location: { [key]: orgDataSession["Line"][key] }, type: "Line" });
    }
    for (const key in orgDataSession?.["Shape"]) {
      locations.push({ location: { [key]: orgDataSession["Shape"][key] }, type: "Shape" });
    }
    for (const key in orgDataSession?.["Point"]) {
      locations.push({ location: { [key]: orgDataSession["Point"][key] }, type: "Point" });
    }

    // // Recursive function to search for routes in the object
    // const findLocations = (obj: any) => {
    //     if (obj && typeof obj === 'object') {
    //         for (const key in obj) {
    //             if (key === "Line") {
    //                 locations.push({ location: obj[key], type: "Line" });
    //             } else if (key === "Shape") {
    //                 locations.push({ location: obj[key], type: "Shape" });
    //             } else if (key === "Point") {
    //                 locations.push({ location: obj[key], type: "Point" });
    //             } else {
    //                 // Recursively search through the properties
    //                 findLocations(obj[key]);
    //             }
    //         }
    //     }
    // };

    // // Start the recursive search
    // findLocations(orgDataSession);

    return locations; // Return the found vehicles wrapped in an object
  }

  useEffect(() => {
    let tempLocationItems: any[] = [];
    // console.log(locations);
    getLocationsFromOrgData(orgDataSession)?.map((location, index) => {
      let keys = Object.keys(location.location);
      keys.map((key: any, keyIndex: number) => {
        let entity = location.location[key];
        let thisIcon: any = <></>;
        // <Polyline points="10,150 30,130 50,120 70,110 90,100 110,120 130,130 150,140 170,130 190,150" stroke="#3290ff" strokeWidth={4} />
        // let key = Object.keys(location.location)[0];
        thisIcon = getPolyLineItemFromPoints(entity?.points, location?.type);

        if (location.type === "Shape") {
          tempLocationItems.push({
            onClick: () => {
              // Note that the points array is just entity.points
              // console.log(entity)
              // This should find the center of the zone, and then update the taskList to be just this navigation task
              // setTaskList([getTaskFromPoints([getCenterOfZone(entity.points)], "Point")]);
            },
            id: index,
            title: key || "",
            subtitle: entity?.zoneType || "",
            icon: thisIcon,
            properties: entity,
          });
        } else if (location.type === "Line") {
          tempLocationItems.push({
            onClick: () => {
              console.log(this);
              // This should create and set a line mission task
              // console.log(entity)
              // setTaskList([getTaskFromPoints(entity.points, "Line")]);
            },
            id: index,
            title: key || "",
            subtitle: entity?.pathType || "",
            icon: thisIcon,
            properties: entity,
          });
        } else if (location.type === "Point") {
          tempLocationItems.push({
            onClick: () => {
              // This should create a navigation task to the point
              // console.log(entity)
              let thisPoint = [{ location: entity.location }];
              // setTaskList([getTaskFromPoints([{ location: entity.location }], "Point")]);
            },
            id: index,
            title: key || "",
            subtitle: entity?.pointType || "",
            icon: thisIcon,
            properties: entity,
          });
        }
      });
    });
    setLocationItems(tempLocationItems);
  }, [orgDataSession]);

  function getPolyLineItemFromPoints(points: any | null, type: string): any {
    // Use shared uniform-scale utility to preserve aspect ratio
    const newPoints = scaleGeoPointsToSvg(points);

    if (type === "Shape") {
      return <Polygon points={newPoints} stroke="#3290ff" strokeWidth={4} />; // Return the array of route items
    } else if (type === "Line") {
      return <Polyline points={newPoints} stroke="#3290ff" strokeWidth={6} />; // Return the array of route items
    } else {
      return (
        <IconMarkerEmpty
          style={{
            position: "relative",
            top: "0px",
            minHeight: "25px",
            minWidth: "25px",
            height: "auto",
            width: "auto",
          }}
        />
      ); // Return the array of route items
    }
  }

  return (
    <div className="MissionPanel">
      <div className="TopPanel">
        <div className="TopTitleBar">
          <div className="TitleBarLeft">
            <IconButton
              icon={<IconChevronLeft />}
              variant="borderless"
              onClick={handleBack}
              style={{ width: "32px", height: "32px", alignSelf: "center" }}
            />
            <div className="MissionTag">
              <b>{editMissionObject?.missionName || "Mission Title"}</b>
            </div>
          </div>
          <div className="exitButtons">
            <IconButton
              icon={<IconHelp size={18} />}
              variant="borderless"
              onClick={() => setShowHelpDialog(true)}
            />
            <IconButton
              icon={panelMode === "one" ? <IconAddColumn size={18} /> : <IconPanelOn size={18} />}
              variant="borderless"
              onClick={handleShowHideList}
            />
            <IconButton
              icon={<IconCircleClose size={18} />}
              variant="borderless"
              onClick={handleBack}
            />
          </div>
        </div>
        <div className="ButtonPanel">
          <Button
            startIcon={
              isAnimating && !isPaused ? <IconPause size={20} /> : <IconVideoPlay size={20} />
            }
            style={{ marginLeft: "6px", marginRight: "6px" }}
            className="PreviewMissionButton"
            disabled={isDisabled}
            onClick={togglePreview}
          >
            {isAnimating && !isPaused
              ? "Pause Preview"
              : isAnimating && isPaused
                ? "Resume Preview"
                : "Preview Mission"}
          </Button>
          <Button
            startIcon={<IconDownload size={20} />}
            style={{ marginLeft: "6px", marginRight: "6px" }}
            className="PreviewMissionButton"
            onClick={exportMission}
            disabled={isDisabled || isExporting}
            loading={isExporting}
          >
            Export
          </Button>
          {hasPermission(role, "MISSION_CREATION") && (
            <Button
              startIcon={<IconSave size={20} />}
              style={{
                marginLeft: "6px",
                marginRight: "6px",
                backgroundColor: "#ffde00",
                color: "black",
                border: "none",
              }}
              className="SaveMissionButton"
              onClick={saveMission}
              disabled={isDisabled || isSaving}
              loading={isSaving}
            >
              Save
            </Button>
          )}
          {hasPermission(role, "MISSION_DELETION") && (
            <Button
              startIcon={<IconDelete size={20} color="#d32f2f" />}
              style={{ marginLeft: "6px", marginRight: "6px" }}
              className="DeleteMissionButton"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isDisabled || isDeleting || !editMissionObject?.missionId}
              loading={isDeleting}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      <div className="textFields">
        <TextField
          label={"Mission Name"}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const missionName = e.target.value;
            setEditMissionObject((prev: any) => ({
              ...prev,
              missionName,
            }));
          }}
          placeholder={"Mission Name"}
          slotProps={{
            input: {
              sx: {
                width: "373px",
              },
            },
          }}
          value={editMissionObject?.missionName || ""}
        />
        <br />
        <br />
        <TextField
          label={"Mission Type"}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const missionType = e.target.value;
            setEditMissionObject((prev: any) => ({
              ...prev,
              missionType,
            }));
          }}
          placeholder={"Mission Type"}
          slotProps={{
            input: {
              sx: {
                width: "373px",
              },
            },
          }}
          value={editMissionObject?.missionType || ""}
        />
        <br />
        <br />
        <TextField
          label={"Mission Description"}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const missionDescription = e.target.value;
            setEditMissionObject((prev: any) => ({
              ...prev,
              missionDescription,
            }));
          }}
          placeholder={"Mission Description"}
          slotProps={{
            input: {
              sx: {
                width: "373px",
              },
            },
          }}
          value={editMissionObject?.missionDescription || ""}
        />
        <br />
        <br />
        <TextField
          label={"Repetitions"}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const taskRepetitions = e.target.value;
            setEditMissionObject((prev: any) => ({
              ...prev,
              taskRepetitions,
            }));
          }}
          placeholder={"1"}
          slotProps={{
            input: {
              sx: {
                width: "373px",
              },
            },
          }}
          value={editMissionObject?.taskRepetitions || ""}
        />
      </div>
      <div className="TaskSection">
        <div className="SectionTitle" style={{ marginLeft: "25px" }}>
          Repeated Tasks
        </div>
        {tasks.map((task, index) => (
          <div
            key={task.id}
            className={`TaskContainer${hasAttemptedSave && isTaskUnconfigured(task) ? " TaskContainerError" : ""}`}
            draggable
            data-index={index}
            onDragStart={() => handleDragStart(index)}
            onDragEnter={() => handleDragEnter(index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => e.preventDefault()}
            onTouchStart={(e) => handleTouchStart(index, e)}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              opacity:
                draggedIndex === index && isTouchDrag
                  ? 0.35
                  : draggedIndex === index
                    ? 0.85
                    : 1,
              cursor: "move",
              touchAction: isTouchDrag ? "none" : "auto",
              boxShadow: draggedIndex === index ? "0px 6px 16px rgba(0, 0, 0, 0.22)" : undefined,
              zIndex: draggedIndex === index ? 2 : undefined,
            }}
          >
            <IconButton
              icon={<IconCircleClose size={12} />}
              style={{ position: "absolute", right: "7px", top: "7px" }}
              variant="borderless"
              onClick={() => removeTask(task.id)}
            />
            {task?.action !== "Navigate" && task?.action !== "Command" && (
              <>
                <div className="SectionText">
                  <div className="SectionSubTitle">Action</div>
                  <DropdownMenu
                    style={{ position: "relative", width: "300px" }}
                    buttonTitle={task?.action || "Select an Action Type"}
                    items={[
                      {
                        onClick: () => {
                          const updatedTasks = tasks.map((t) => {
                            if (t.id === task.id) {
                              return { ...t, action: "Navigate" };
                            }
                            return t;
                          });
                          setTasks(updatedTasks);
                        },
                        id: "0",
                        title: "Navigate",
                        subtitle: "Drive to an Element",
                        // icon: <Polyline points="10,50 30,40 50,30 70,50 90,70 110,60 130,50 150,60 170,80 190,50" stroke="#3290ff" strokeWidth={4} />,
                        icon: <IconJobDetailsGuidance size={40} />,
                      },
                      {
                        onClick: () => {
                          const updatedTasks = tasks.map((t) => {
                            if (t.id === task.id) {
                              return { ...t, action: "Command" };
                            }
                            return t;
                          });
                          setTasks(updatedTasks);
                        },
                        id: "1",
                        title: "Execute",
                        subtitle: "Dump, Dig, Load, etc.",
                        icon: <StatusAutonomyOff size={40} />,
                      },
                    ]}
                  />
                </div>
              </>
            )}
            {task?.action === "Navigate" && (
              <>
                <div className="SectionIcon">
                  <div>
                    {task.icon ? (
                      task.icon
                    ) : (
                      <IconJobDetailsGuidance
                        style={{
                          minHeight: "32px",
                          minWidth: "32px",
                          height: "auto",
                          width: "auto",
                          opacity: 0.65,
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="SectionText">
                  <div className="SectionSubTitle">Destination</div>
                  <DropdownMenu
                    buttonTitle={task.title || "Select a Destination"}
                    items={locationItems.map((item: any) => ({
                      ...item,
                      onClick: () => {
                        console.log(item); // Log the clicked item
                        const updatedTasks = tasks.map((t) => {
                          if (t.id === task.id) {
                            return {
                              ...t,
                              navigateItem: item,
                              title: item.title,
                              properties: item.properties,
                              icon: item.icon,
                            };
                          }
                          return t;
                        });
                        setTasks(updatedTasks);
                        console.log(item.properties);
                        // Additional logic can go here
                      },
                    }))}
                    // disabled
                  />
                </div>
              </>
            )}
            {task?.action === "Command" && (
              <>
                <div className="SectionIcon">
                  <div>
                    {task.icon ? (
                      task.icon
                    ) : (
                      <StatusAutonomyOff
                        style={{
                          minHeight: "32px",
                          minWidth: "32px",
                          height: "auto",
                          width: "auto",
                          opacity: 0.65,
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="SectionText">
                  <div className="SectionSubTitle">Execute</div>
                  <DropdownMenu
                    buttonTitle={task.title || "Select a Command"}
                    items={commandItems.map((item: any) => ({
                      ...item,
                      onClick: () => {
                        console.log(item); // Log the clicked item
                        const updatedTasks = tasks.map((t) => {
                          if (t.id === task.id) {
                            return {
                              ...t,
                              title: item.title,
                              properties: {
                                ...item.properties,
                                // Preserve existing loadSide if switching back to GetLoaded;
                                // leave undefined so user must explicitly select a direction.
                                ...(item.properties?.workType === "GetLoaded" &&
                                t.properties?.loadSide !== undefined
                                  ? { loadSide: t.properties.loadSide }
                                  : {}),
                              },
                              icon: item.icon,
                            };
                          }
                          return t;
                        });
                        setTasks(updatedTasks);
                        console.log(item.properties);
                      },
                    }))}
                    // disabled
                  />
                  {task.properties?.workType === "GetLoaded" && (
                    <div
                      className={`LoadSideSelector ${
                        task.properties?.loadSide === undefined ? "LoadSideSelectorRequired" : ""
                      }`}
                    >
                      <div className="LoadSideHeader">
                        <div className="SectionSubTitle" style={{ marginTop: "12px" }}>
                          Load Direction
                          <span className="LoadSideRequiredBadge">Required</span>
                        </div>
                        {task.properties?.loadSide === undefined && (
                          <div className="LoadSideWarning">
                            Select which side the loader will load from
                          </div>
                        )}
                      </div>
                      <div className="LoadSideOptions">
                        {[
                          { value: 0, label: "Driver's Left", icon: "◀" },
                          { value: 1, label: "Driver's Right", icon: "▶" },
                          { value: 2, label: "Rear", icon: "▼" },
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`LoadSideOption ${
                              task.properties?.loadSide === option.value
                                ? "LoadSideOptionActive"
                                : ""
                            }`}
                            onClick={() => {
                              const updatedTasks = tasks.map((t) => {
                                if (t.id === task.id) {
                                  return {
                                    ...t,
                                    properties: {
                                      ...t.properties,
                                      loadSide: option.value,
                                    },
                                  };
                                }
                                return t;
                              });
                              setTasks(updatedTasks);
                            }}
                          >
                            <span className="LoadSideOptionIcon">{option.icon}</span>
                            <span className="LoadSideOptionLabel">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        <button
          onClick={addTask}
          style={{
            position: "relative",
            left: "32px",
            color: "green",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          + Select Additional Action
        </button>
      </div>

      <Dialog
        open={showHelpDialog}
        title="How to Set Up a Mission"
        footerActions={[
          {
            title: "Got it",
            onClick: () => setShowHelpDialog(false),
            variant: "primary",
          } as FooterAction,
        ]}
        closeHandler={() => setShowHelpDialog(false)}
        width="md"
      >
        <div className="helpTutorialContent">
          <h4 className="helpSectionHeader">Setting Up Jobsite Locations</h4>
          <p>Before building a mission, create the locations your machines will navigate to:</p>
          <ol className="helpStepsList">
            <li>
              <strong>Open the Jobsite panel</strong> and go to the <em>"Attributes"</em> tab.
            </li>
            <li>
              <strong>Add Points, Lines, or Shapes</strong> by clicking the <strong>+</strong>{" "}
              button:
              <ul>
                <li>
                  <strong>Point</strong> — A single location (e.g. Dump Point, Load Point). Click
                  the map to place it and set a heading.
                </li>
                <li>
                  <strong>Line</strong> — A path made of sequential waypoints (e.g. Navigable Path,
                  Windrow, Static Route). Click the map to add each waypoint.
                </li>
                <li>
                  <strong>Shape</strong> — A zone or boundary (e.g. Work Zone, Free Space Zone,
                  Restricted Zone). Click the map to draw the polygon.
                </li>
              </ul>
            </li>
            <li>
              Give each location a <strong>name</strong>, <strong>description</strong>, and{" "}
              <strong>type</strong>, then save. These will appear as Navigate destinations when
              building missions.
            </li>
          </ol>

          <h4 className="helpSectionHeader">Creating a Mission</h4>
          <p>Follow these steps to create and configure a mission:</p>
          <ol className="helpStepsList">
            <li>
              <strong>Name your mission</strong>
              <br />
              Fill in the Mission Name, Mission Type, Description, and number of Repetitions in the
              text fields at the top of the panel.
            </li>
            <li>
              <strong>Add tasks</strong>
              <br />
              Click <em>"+ Select Additional Action"</em> at the bottom to add tasks to your
              mission. Each task represents one step for the machine to execute.
            </li>
            <li>
              <strong>Configure each task</strong>
              <br />
              Choose an <strong>Action Type</strong> for each task:
              <ul>
                <li>
                  <strong>Navigate</strong> — Drive to a map element. Select a destination from
                  available Points, Lines, or Shapes on your jobsite.
                </li>
                <li>
                  <strong>Execute</strong> — Perform a command: Dump, Work On, Work Off, Wait to
                  Enter, or Load Truck.
                </li>
              </ul>
            </li>
            <li>
              <strong>Reorder tasks</strong>
              <br />
              Drag and drop tasks to change the execution order.
            </li>
            <li>
              <strong>Preview your mission</strong>
              <br />
              Click <em>"Preview Mission"</em> to animate the planned route on the map and verify
              the path.
            </li>
            <li>
              <strong>Save or Export</strong>
              <br />
              Click <em>"Save"</em> to persist the mission, or <em>"Export"</em> to download it as a
              JSON file.
            </li>
          </ol>

          <h4 className="helpSectionHeader">Applying a Mission to a Machine</h4>
          <p>Once a mission is saved, assign it to a machine from the Equipment panel:</p>
          <ol className="helpStepsList">
            <li>
              <strong>Select a machine</strong>
              <br />
              In the Jobsite panel <em>"Equipment"</em> tab, click on a machine to open its
              Equipment panel.
            </li>
            <li>
              <strong>Choose a mission</strong>
              <br />
              In the <em>"Right Now"</em> tab, open the <em>"Select a Mission"</em> dropdown and
              pick the mission you created.
            </li>
            <li>
              <strong>Optionally pick a starting task</strong>
              <br />
              Click the <em>"Edit"</em> button next to the mission dropdown to open the task
              selector. Choose which task to start from — all previous tasks will be skipped.
            </li>
            <li>
              <strong>Complete the checklist</strong>
              <br />
              Ensure all pre-flight checks are met:
              <ul>
                <li>Autonomy is enabled on the machine</li>
                <li>You are standing within 1,500 ft of the machine</li>
                <li>No errors are reported</li>
              </ul>
            </li>
            <li>
              <strong>Start Autonomy</strong>
              <br />
              Click <em>"Start Autonomy"</em>, then confirm with <em>"Begin Work"</em> in the
              confirmation dialog. The mission will be sent to the machine and execution begins.
            </li>
            <li>
              <strong>Monitor and control</strong>
              <br />
              While the mission is active you can <em>"Pause"</em>, <em>"Resume"</em>, or{" "}
              <em>"Quit Mission"</em> from the same panel.
            </li>
          </ol>

          <h4 className="helpSectionHeader">Quick Navigate ("Go To")</h4>
          <p>
            To send a machine to a single location without a full mission, click <em>"Go To"</em> in
            the Equipment panel header, select a destination from the list, then click{" "}
            <em>"Start Autonomy"</em>.
          </p>
        </div>
      </Dialog>

      <Dialog
        open={showDeleteDialog}
        closeHandler={() => setShowDeleteDialog(false)}
        title="Delete Mission"
        width="sm"
        footerActions={[
          {
            title: "Cancel",
            variant: "secondary",
            onClick: (): void => {
              setShowDeleteDialog(false);
            },
          },
          {
            title: "Delete",
            variant: "primary",
            disabled: isDeleting,
            loading: isDeleting,
            onClick: (): void => {
              void deleteMission();
            },
          },
        ]}
      >
        {(() => {
          const activeOn = activeMachinesRunningMission;
          const machineNames = activeOn.map((id) => id.split("/").pop() || id);
          const machineLabel = machineNames.join(", ");
          return (
            <div>
              <p>
                Are you sure you want to delete mission{" "}
                <strong>{editMissionObject?.missionName || editMissionObject?.missionId}</strong>?
              </p>
              {activeOn.length > 0 && (
                <p style={{ marginTop: "12px" }}>
                  This mission is currently running on <strong>{machineLabel}</strong>. Deleting it
                  will not stop the machine — it will only remove the mission from your list.
                </p>
              )}
            </div>
          );
        })()}
      </Dialog>
    </div>
  );
};

export default MissionPanel;
